/**
 * Google linking scopes for Classroom grading plus final-action Drive saves.
 *
 * `classroom.profile.emails` is required so the Classroom roster API
 * returns each student's `profile.emailAddress`. Without it, the
 * `students.list` response simply omits the email field — even though the
 * underlying account has one — and the publish flow falls back to
 * "Email skipped — no addresses available". The scope is additive: every
 * existing scope still ships (Classroom courses, rosters, coursework,
 * student submissions read, plus Drive file). Existing teachers who
 * connected before this scope was added will see a "Reconnect Google to
 * email students" prompt on the publish screen — they keep all current
 * permissions until they reconnect.
 *
 * `gmail.send` is required at runtime so DoneGrading can actually
 * deliver graded feedback via the linked teacher's Gmail account.
 * Without it, the publish flow blocks at the Gmail API and the results
 * screen surfaces a "Reconnect Google to send feedback emails" card.
 * The scope must also be listed in the Google Cloud OAuth consent
 * screen / Data Access configuration; missing it there means consent
 * silently drops the scope and Reconnect Google won't fix the problem.
 *
 * Classroom coursework read scopes are split four ways by Google
 * (NEVER assume one implies another — `coursework.students` does NOT
 * grant `coursework.students.readonly` and vice versa):
 *
 *   - `classroom.coursework.students`           (write/manage)
 *   - `classroom.coursework.students.readonly`  (read assignments as a teacher)
 *   - `classroom.coursework.me`                 (write/manage own coursework)
 *   - `classroom.coursework.me.readonly`        (read own coursework)
 *
 * We request all four so coursework.list works regardless of whether
 * the linked account is acting as a teacher (`students.readonly`) or
 * a student/admin context (`me.readonly`). Google grants the union of
 * what the user approves on the consent screen, and the scope flags
 * surfaced from /api/google/status let the UI prompt reconnect when
 * the actual stored grant is missing one of these.
 */
export const GOOGLE_CLASSROOM_PROFILE_EMAILS_SCOPE =
  'https://www.googleapis.com/auth/classroom.profile.emails';

export const GOOGLE_GMAIL_SEND_SCOPE = 'https://www.googleapis.com/auth/gmail.send';

export const GOOGLE_DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

export const GOOGLE_CLASSROOM_COURSES_READONLY_SCOPE =
  'https://www.googleapis.com/auth/classroom.courses.readonly';

/**
 * Manage Google Classroom courses (create / update / archive). Required
 * for `courses.create` calls from the server. Distinct from
 * `classroom.courses.readonly` — Google grants the union of approved
 * scopes, so we request both and surface a clear "Reconnect Google"
 * prompt when the stored grant carries only the readonly variant.
 */
export const GOOGLE_CLASSROOM_COURSES_SCOPE = 'https://www.googleapis.com/auth/classroom.courses';

export const GOOGLE_CLASSROOM_ROSTERS_READONLY_SCOPE =
  'https://www.googleapis.com/auth/classroom.rosters.readonly';

export const GOOGLE_CLASSROOM_COURSEWORK_STUDENTS_SCOPE =
  'https://www.googleapis.com/auth/classroom.coursework.students';

export const GOOGLE_CLASSROOM_COURSEWORK_STUDENTS_READONLY_SCOPE =
  'https://www.googleapis.com/auth/classroom.coursework.students.readonly';

export const GOOGLE_CLASSROOM_COURSEWORK_ME_SCOPE =
  'https://www.googleapis.com/auth/classroom.coursework.me';

export const GOOGLE_CLASSROOM_COURSEWORK_ME_READONLY_SCOPE =
  'https://www.googleapis.com/auth/classroom.coursework.me.readonly';

export const GOOGLE_CLASSROOM_STUDENT_SUBMISSIONS_STUDENTS_READONLY_SCOPE =
  'https://www.googleapis.com/auth/classroom.student-submissions.students.readonly';

/**
 * Phase B: Google Classroom topic management. `classroom.topics`
 * grants both list + create on `courses.topics`; the dedicated
 * `classroom.topics.readonly` scope only grants list. Phase B needs
 * BOTH list and create, so we request the write scope (the readonly
 * variant is intentionally NOT added — Google grants the union of
 * approved scopes and the write scope subsumes the readonly's
 * capabilities for our use).
 */
export const GOOGLE_CLASSROOM_TOPICS_SCOPE = 'https://www.googleapis.com/auth/classroom.topics';

export const GOOGLE_USERINFO_EMAIL_SCOPE = 'https://www.googleapis.com/auth/userinfo.email';

export const GOOGLE_USERINFO_PROFILE_SCOPE = 'https://www.googleapis.com/auth/userinfo.profile';

/**
 * Ordered list of scopes the GIS code client requests on Reconnect /
 * initial link. Exposed as an array (not just the joined string) so dev
 * logging and unit tests can count + introspect membership without
 * re-parsing.
 *
 * Order matches the spec for the "missing scopes after grant" investigation
 * so reading the array in dev logs lines up with the audit list.
 */
export const GOOGLE_CLASSROOM_LINK_SCOPE_LIST: readonly string[] = Object.freeze([
  'openid',
  'email',
  'profile',
  GOOGLE_USERINFO_EMAIL_SCOPE,
  GOOGLE_USERINFO_PROFILE_SCOPE,
  GOOGLE_CLASSROOM_COURSES_READONLY_SCOPE,
  GOOGLE_CLASSROOM_COURSES_SCOPE,
  GOOGLE_CLASSROOM_ROSTERS_READONLY_SCOPE,
  GOOGLE_CLASSROOM_PROFILE_EMAILS_SCOPE,
  GOOGLE_CLASSROOM_COURSEWORK_STUDENTS_SCOPE,
  GOOGLE_CLASSROOM_COURSEWORK_STUDENTS_READONLY_SCOPE,
  GOOGLE_CLASSROOM_COURSEWORK_ME_SCOPE,
  GOOGLE_CLASSROOM_COURSEWORK_ME_READONLY_SCOPE,
  GOOGLE_CLASSROOM_STUDENT_SUBMISSIONS_STUDENTS_READONLY_SCOPE,
  GOOGLE_CLASSROOM_TOPICS_SCOPE,
  GOOGLE_DRIVE_FILE_SCOPE,
  GOOGLE_GMAIL_SEND_SCOPE,
]);

/** Space-separated scope string in the exact shape `initCodeClient` expects. */
export const GOOGLE_CLASSROOM_LINK_SCOPES = GOOGLE_CLASSROOM_LINK_SCOPE_LIST.join(' ');
